<template>
  <div class="row">
    <div class="col-12">
      <page-header />
      <div v-if="hasAdminAccess" class="row page-section">
        <div class="col-12">
          <div class="row section-header">
            <div class="col-12">
              <h1>{{ 'new_catalogue' | i18n }}</h1>
            </div>
          </div>
          <upload-catalogue @uploaded="onUploaded" />
        </div>
      </div>
      <div class="row page-section">
        <div class="col-12">
          <div class="row section-header">
            <div class="col-12 col-lg-6">
              <h1>{{ 'catalogues' | i18n }}</h1>
            </div>
            <div class="col-12 col-lg-6">
              <div v-if="hasAdminAccess" class="action-buttons">
                <b-button
                  v-if="!table.selectedRow || !table.selectedRow.editMode"
                  :disabled="!table.selectedRow"
                  variant="primary"
                  size="sm"
                  @click="toggleEditMode(table.selectedRow)"
                >
                  {{ 'edit' | i18n }}
                </b-button>
                <b-button
                  v-if="table.selectedRow && table.selectedRow.editMode"
                  :disabled="!isEditedRowValid"
                  variant="primary"
                  size="sm"
                  @click="updateRow(table.selectedRow)"
                >
                  {{ 'save' | i18n }}
                </b-button>
                <b-button
                  v-if="table.selectedRow && table.selectedRow.editMode"
                  variant="outline-primary"
                  size="sm"
                  @click="toggleEditMode(table.selectedRow)"
                >
                  {{ 'abort' | i18n }}
                </b-button>
                <b-button size="sm" variant="outline-primary" @click="deleteSelected">
                  {{ 'delete' | i18n }}
                </b-button>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12 main-content">
              <data-table ref="table" v-model="table" :get-page="getPage" :selectable-rows="true">
                <template v-slot:cell(name)="row">
                  <span v-if="!row.item.editMode">{{ row.item.name }}</span>
                  <b-form-group v-if="row.item.editMode" id="catalogue-name">
                    <b-form-input id="catalogue-name-input" v-model="table.editedRow.name" :placeholder="'name' | i18n" size="sm" trim />
                  </b-form-group>
                </template>
                <template v-slot:cell(description)="row">
                  <span v-if="!row.item.editMode">{{ row.item.description }}</span>
                  <b-form-group v-if="row.item.editMode" id="catalogue-description">
                    <b-form-input
                      id="catalogue-description-input"
                      v-model="table.editedRow.description"
                      :placeholder="'description' | i18n"
                      size="sm"
                      trim
                    />
                  </b-form-group>
                </template>
                <template v-slot:cell(createdAt)="row">
                  {{ row.item.createdAt | formatDate('DD.MM.YY HH:mm') }}
                </template>
                <template v-slot:cell(updatedAt)="row">
                  {{ row.item.updatedAt | formatDate('DD.MM.YY HH:mm') }}
                </template>
                <template v-slot:cell(download)="row">
                  <a v-b-tooltip.hover href="#" :title="'download' | i18n" @click="downloadCatalogue(row.item)"><img :src="downloadIcon"/></a>
                </template>
              </data-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CatalogueMixin, DeleteRowMixin, AuthMixin } from '../../mixins';
import UploadCatalogue from './upload-catalogue';
import { STATUS_CODES } from '../../../../../constants';
import downloadIcon from '../../assets/icons/download.svg';
import HttpError from '../../services/HttpError';
import DataTable from '../table/data-table';
import PageHeader from '../structure/page-header';

export default {
  name: 'CatalogueManagement',
  components: { PageHeader, DataTable, UploadCatalogue },
  mixins: [CatalogueMixin, DeleteRowMixin, AuthMixin],
  data: function() {
    return {
      downloadIcon: downloadIcon,
      table: {
        editedRow: null,
        selectedRow: null,
        rows: [],
        fields: [
          { key: 'check', label: '', thClass: 'check', tdClass: 'check' },
          { key: 'name', label: this.$translate('name'), thClass: 'catalogue-name', tdClass: 'catalogue-name' },
          { key: 'description', label: this.$translate('description'), thClass: 'catalogue-description', tdClass: 'catalogue-description' },
          { key: 'createdAt', label: this.$translate('created_date'), thClass: 'catalogue-date', tdClass: 'catalogue-date' },
          { key: 'updatedAt', label: this.$translate('updated_date'), thClass: 'catalogue-date', tdClass: 'catalogue-date' },
          { key: 'download', label: '', thClass: 'download', tdClass: 'download' }
        ]
      }
    };
  },
  computed: {
    isEditedRowValid: function() {
      return this.table.editedRow && this.table.editedRow.name && this.table.editedRow.description;
    }
  },
  methods: {
    async getPage(ctx) {
      try {
        const response = await this.catalogueService.getPage({
          page: ctx.currentPage,
          limit: ctx.perPage
        });
        return { totalCount: response.totalCount, data: response.catalogues };
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    onUploaded() {
      this.$refs.table.refresh();
    },
    async downloadCatalogue(catalogue) {
      try {
        await this.catalogueService.downloadCatalogue(catalogue.filename);
      } catch (err) {
        const message = err instanceof HttpError && err.status === STATUS_CODES.NOT_FOUND ? this.$translate('not_found') : null;
        this.showNotification({ type: 'error', title: this.$translate('download'), message: message });
      }
    },
    async deleteSelected() {
      const selectedItems = this.table.rows.filter(c => c.isSelected);
      await this.deleteSelectedRows(
        {
          message: this.$translate('catalogues_delete_confirmation_message'),
          rowDisplayValues: selectedItems.map(p => p.name)
        },
        async () => {
          await this.catalogueService.deleteCatalogues({ ids: selectedItems.map(c => c.id) });
          this.$refs.table.refresh();
        }
      );
    },
    async updateRow(row) {
      try {
        await this.catalogueService.updateCatalogue(row.id, {
          name: this.table.editedRow.name,
          description: this.table.editedRow.description
        });
        row.editMode = false;
        this.$refs.table.toggleSelectRow(this.table.selectedRow);
        this.$refs.table.refresh();
        this.showNotification({
          type: 'success',
          title: this.$translate('catalogue_update'),
          message: this.$translate('catalogue_update_success_message')
        });
      } catch (err) {
        this.showNotification({ type: 'error', title: this.$translate('catalogue_update'), message: err.message });
      }
    },
    toggleEditMode(item) {
      if (!item.editMode) {
        this.table.rows.forEach(item => (item.editMode = false));
      }
      item.editMode = !item.editMode;
      this.table.editedRow = { ...item };
    }
  }
};
</script>

<style scoped lang="scss">
::v-deep   .table {
  th,
  td {
    &.check,
    &.download {
      width: 5%;
    }

    &.catalogue-name {
      width: 25%;
    }

    &.catalogue-description {
      width: 35%;
    }

    &.catalogue-date {
      width: 15%;
    }
  }
}

.section-header {
  margin-top: 10px;
}

::v-deep   .b-table-sticky-header {
  max-height: calc(100vh - 550px);
  min-height: calc(100vh - 550px);
}
</style>
